/**
 * Create the Lost Report Upload & Extract Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createLostReportUploadExtractAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  // Track current phase: 'upload' or 'extract'
  let currentPhase: 'upload' | 'extract' = 'upload';
  // Store extracted data
  let extractedData: { plateColor?: string; plateNumber?: string; plateSource?: string; plateCategory?: string } = {};

  return {
    id: 'lost_report_upload_extract_agent',
    name: 'Lost Report Upload & Extract Agent',
    description: 'Combined: Show upload UI, wait for upload, extract data from image',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger showing upload UI
    initialPrompt: 'Start the lost report replacement process. IMMEDIATELY call show_license_upload to display the upload UI, then explain to the user what photo they need to upload (clear vehicle license front side).',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['data_confirmed', {
          target: { agentId: 'lost_report_search_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'show_license_upload') {
        console.log('[LostReportUploadExtractAgent] Showing license upload UI');
        currentPhase = 'upload';
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'License upload UI is now visible. User can upload vehicle license image.',
          },
        };
      }
      
      if (toolName === 'extract_license_data') {
        extractedData = {
          plateColor: args.plateColor,
          plateNumber: args.plateNumber,
          plateSource: args.plateSource,
          plateCategory: args.plateCategory,
        };
        console.log('[LostReportUploadExtractAgent] Data extracted:', extractedData);
        currentPhase = 'extract';
        
        return {
          toolName,
          args,
          result: {
            success: true,
            extracted_data: extractedData,
            message: 'Data extracted. Guide user to review and confirm.',
          },
        };
      }
      
      if (toolName === 'data_confirmed') {
        console.log('[LostReportUploadExtractAgent] Data confirmed, proceeding to search');
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Proceeding to date range selection',
          },
          triggersTransition: true,
          transitionTo: 'lost_report_search_agent',
          transitionData: {
            licenseData: extractedData,
          },
        };
      }
      
      if (toolName === 'reupload_license') {
        console.log('[LostReportUploadExtractAgent] User wants to re-upload');
        extractedData = {}; // Clear data
        currentPhase = 'upload';
        
        return {
          toolName,
          args,
          result: { 
            success: true, 
            message: 'Reset to upload phase. Call show_license_upload again.',
            reset_to_upload: true,
          },
          triggersTransition: false, // Stay in same agent
        };
      }
      
      if (toolName === 'cancel_lost_report') {
        console.log('[LostReportUploadExtractAgent] User cancelled');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            licenseData: undefined,
          },
        };
      }
      
      // Inline FAQ
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[LostReportUploadExtractAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: currentPhase === 'upload' 
              ? 'Answer the question, then guide user back to uploading.'
              : 'Answer the question, then guide user to confirm data.',
          },
          triggersTransition: false,
        };
      }
      
      // Universal navigation: Switch to accident reporting
      if (toolName === 'start_accident_reporting') {
        console.log('[LostReportUploadExtractAgent] Switching to accident reporting');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Universal navigation: Switch to renewal
      if (toolName === 'start_renewal') {
        console.log('[LostReportUploadExtractAgent] Switching to renewal');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to renewal' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
        };
      }
      
      // Universal navigation: Switch to FAQ
      if (toolName === 'go_to_faq') {
        console.log('[LostReportUploadExtractAgent] Switching to FAQ');
        return {
          toolName,
          args,
          result: { success: true, message: 'Switching to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      currentPhase = 'upload';
      extractedData = {}; // Reset on activation
      console.log('[LostReportUploadExtractAgent] Activated - Combined upload & extract flow');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[LostReportUploadExtractAgent] Deactivated');
    },
  };
}

export default createLostReportUploadExtractAgent;
